import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Form,
  Input,
  Button,
  message,
  Spin,
  Progress,
  Table,
  Select,
} from 'antd';
import {
  EnvironmentOutlined,
  DropboxOutlined,
  BugOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
} from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

interface ConservationMetrics {
  id: string;
  waterSaved?: number;
  waterEfficiency?: number;
  soilCarbonSequestration?: number;
  biodiversityScore?: number;
  energySaved?: number;
  totalCarbonOffset?: number;
  conservationScore?: number;
  timestamp: string;
}

interface ImpactSummary {
  totalWaterSaved: number;
  totalCarbonOffset: number;
  avgBiodiversityScore: number;
  totalEnergySaved: number;
  conservationScore: number;
}

const Conservation: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState<ConservationMetrics[]>([]);
  const [impactSummary, setImpactSummary] = useState<ImpactSummary | null>(null);
  const [score, setScore] = useState<any>(null);
  const [selectedFarmId, setSelectedFarmId] = useState<string>('');

  useEffect(() => {
    if (user) {
      fetchMetrics();
    }
  }, [user]);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/conservation/metrics`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setMetrics(response.data.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      message.error('Failed to load conservation metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchImpactSummary = async (farmId: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const [summaryRes, scoreRes] = await Promise.all([
        axios.get(
          `${import.meta.env.VITE_API_URL}/api/conservation/impact/${farmId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        axios.get(
          `${import.meta.env.VITE_API_URL}/api/conservation/score/${farmId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ]);
      setImpactSummary(summaryRes.data.data);
      setScore(scoreRes.data.data);
    } catch (error) {
      console.error('Error fetching impact summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordMetrics = async (values: any) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/conservation/metrics`,
        {
          farmId: values.farmId,
          waterSaved: values.waterSaved,
          waterEfficiency: values.waterEfficiency,
          soilCarbonSequestration: values.soilCarbonSequestration,
          organicMatterIncrease: values.organicMatterIncrease,
          biodiversityScore: values.biodiversityScore,
          speciesCount: values.speciesCount,
          energySaved: values.energySaved,
          energyEfficiencyGain: values.energyEfficiencyGain,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      message.success('Conservation metrics recorded successfully');
      fetchMetrics();
      if (values.farmId) {
        fetchImpactSummary(values.farmId);
      }
    } catch (error) {
      message.error('Failed to record conservation metrics');
    } finally {
      setLoading(false);
    }
  };

  const metricsColumns = [
    {
      title: 'Date',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: string) => new Date(timestamp).toLocaleDateString(),
    },
    {
      title: 'Water Saved',
      dataIndex: 'waterSaved',
      key: 'waterSaved',
      render: (value: number) => value ? `${value.toFixed(0)} gallons` : '-',
    },
    {
      title: 'Carbon Offset',
      dataIndex: 'totalCarbonOffset',
      key: 'totalCarbonOffset',
      render: (value: number) => value ? `${value.toFixed(2)} tons CO₂` : '-',
    },
    {
      title: 'Biodiversity Score',
      dataIndex: 'biodiversityScore',
      key: 'biodiversityScore',
      render: (value: number) => value ? `${value.toFixed(1)}/100` : '-',
    },
    {
      title: 'Conservation Score',
      dataIndex: 'conservationScore',
      key: 'conservationScore',
      render: (value: number) => value ? `${value.toFixed(1)}/100` : '-',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Conservation Dashboard</Title>

      {impactSummary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Water Saved"
                value={impactSummary.totalWaterSaved.toFixed(0)}
                suffix="gallons"
                prefix={<DropboxOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Carbon Offset"
                value={impactSummary.totalCarbonOffset.toFixed(2)}
                suffix="tons CO₂"
                prefix={<EnvironmentOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Biodiversity Score"
                value={impactSummary.avgBiodiversityScore.toFixed(1)}
                suffix="/100"
                prefix={<BugOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Conservation Score"
                value={impactSummary.conservationScore.toFixed(1)}
                suffix="/100"
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {score && (
        <Card title="Conservation Score Trend" style={{ marginBottom: 24 }}>
          <Progress
            percent={score.score}
            status="active"
            format={(percent) => `${percent?.toFixed(1)}/100`}
          />
        </Card>
      )}

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card title="Record Conservation Metrics">
            <Form onFinish={handleRecordMetrics} layout="vertical">
              <Form.Item
                name="farmId"
                label="Farm ID"
                rules={[{ required: true }]}
              >
                <Input
                  placeholder="Enter farm ID"
                  onChange={(e) => setSelectedFarmId(e.target.value)}
                />
              </Form.Item>
              <Form.Item name="waterSaved" label="Water Saved (gallons)">
                <Input type="number" />
              </Form.Item>
              <Form.Item name="waterEfficiency" label="Water Efficiency (%)">
                <Input type="number" min={0} max={100} />
              </Form.Item>
              <Form.Item name="soilCarbonSequestration" label="Soil Carbon Sequestration (tons CO₂)">
                <Input type="number" step="0.01" />
              </Form.Item>
              <Form.Item name="organicMatterIncrease" label="Organic Matter Increase (%)">
                <Input type="number" min={0} max={100} />
              </Form.Item>
              <Form.Item name="biodiversityScore" label="Biodiversity Score (0-100)">
                <Input type="number" min={0} max={100} />
              </Form.Item>
              <Form.Item name="speciesCount" label="Species Count">
                <Input type="number" />
              </Form.Item>
              <Form.Item name="energySaved" label="Energy Saved (kWh)">
                <Input type="number" />
              </Form.Item>
              <Form.Item name="energyEfficiencyGain" label="Energy Efficiency Gain (%)">
                <Input type="number" min={0} max={100} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Record Metrics
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Recent Metrics">
            <Table
              columns={metricsColumns}
              dataSource={metrics}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </Col>
      </Row>

      {selectedFarmId && (
        <Card
          title="View Impact Summary"
          extra={
            <Button onClick={() => fetchImpactSummary(selectedFarmId)}>
              Refresh
            </Button>
          }
          style={{ marginTop: 16 }}
        >
          <Button onClick={() => fetchImpactSummary(selectedFarmId)}>
            Load Impact Summary for Farm {selectedFarmId}
          </Button>
        </Card>
      )}
    </div>
  );
};

export default Conservation;

